import { useEffect, useState, useCallback } from 'react'
import { listDomains } from '@/api/domains'
import { IdentityInfo, ListDomainsParams } from '@/lib/types'
import { DomainTable } from '@/components/domains/domain-table'
import { Skeleton } from '@/components/ui/skeleton'

const PAGE_SIZE = 50

export interface AppliedFilters {
  search: string
  accountId: string
  status: string
}

export function DomainsPage() {
  const [domains, setDomains] = useState<IdentityInfo[]>([])
  const [loading, setLoading] = useState(true)
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [total, setTotal] = useState(0)
  const [currentPage, setCurrentPage] = useState(1)
  // cursors[i] is the cursor needed to fetch page i+1 (cursors[0] = undefined = page 1)
  const [cursors, setCursors] = useState<(string | undefined)[]>([undefined])
  const [searchParams, setSearchParams] = useState<ListDomainsParams>({
    limit: PAGE_SIZE,
  })
  const [appliedFilters, setAppliedFilters] = useState<AppliedFilters>({
    search: '',
    accountId: '',
    status: 'all',
  })

  const fetchDomains = useCallback(async (
    params: ListDomainsParams,
    page: number,
    cursor: string | undefined,
    options?: { refresh?: boolean },
  ) => {
    const { refresh = false } = options || {}

    if (refresh) {
      setIsRefreshing(true)
    } else {
      setLoading(true)
    }

    const result = await listDomains({ ...params, cursor })
    if (result.success && result.data) {
      setDomains(result.data.domains)
      if (page === 1 || result.data.total > 0) setTotal(result.data.total)
      setCurrentPage(page)
      if (result.data.nextCursor) {
        setCursors(prev => {
          const next = [...prev]
          next[page] = result.data!.nextCursor
          return next
        })
      }
      setError(null)
    } else {
      setError(result.error || 'Failed to fetch domains')
    }

    setLoading(false)
    setIsRefreshing(false)
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchDomains(searchParams, 1, undefined)
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const handleApplyFilters = useCallback((filters: AppliedFilters) => {
    const newParams: ListDomainsParams = {
      limit: PAGE_SIZE,
      search: filters.search || undefined,
      account_id: filters.accountId || undefined,
      status: filters.status === 'all' ? undefined : filters.status,
    }
    setSearchParams(newParams)
    setCursors([undefined])
    fetchDomains(newParams, 1, undefined)
    setAppliedFilters(filters)
  }, [fetchDomains])

  const handlePageChange = useCallback(async (targetPage: number) => {
    // Find the highest page we already have a cursor for
    let startPage = 1
    for (let i = targetPage - 1; i >= 1; i--) {
      if (i === 1 || cursors[i - 1] !== undefined) {
        startPage = i
        break
      }
    }

    let updatedCursors = cursors
    if (startPage < targetPage) {
      // Chain-fetch intermediate pages to collect missing cursors
      setIsRefreshing(true)
      updatedCursors = [...cursors]
      for (let p = startPage; p < targetPage; p++) {
        const cursor = p === 1 ? undefined : updatedCursors[p - 1]
        const result = await listDomains({ ...searchParams, cursor })
        if (!result.success || !result.data?.nextCursor) {
          setIsRefreshing(false)
          return
        }
        updatedCursors[p] = result.data.nextCursor
      }
      setCursors(updatedCursors)
    }

    fetchDomains(searchParams, targetPage, updatedCursors[targetPage - 1], { refresh: true })
  }, [cursors, searchParams, fetchDomains])

  const handleRefresh = useCallback(() => {
    setCursors([undefined])
    fetchDomains(searchParams, 1, undefined, { refresh: true })
  }, [searchParams, fetchDomains])

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-48" />
        <Skeleton className="h-10 w-full" />
        <div className="space-y-2">
          {[1, 2, 3, 4, 5].map((i) => (
            <Skeleton key={i} className="h-16 w-full" />
          ))}
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <p className="text-destructive mb-4">{error}</p>
        <button
          onClick={handleRefresh}
          className="text-primary hover:underline"
        >
          Try again
        </button>
      </div>
    )
  }

  const totalPages = Math.ceil(total / PAGE_SIZE)

  return (
    <DomainTable
      domains={domains}
      total={total}
      currentPage={currentPage}
      totalPages={totalPages}
      isRefreshing={isRefreshing}
      appliedFilters={appliedFilters}
      onRefresh={handleRefresh}
      onApplyFilters={handleApplyFilters}
      onPageChange={handlePageChange}
    />
  )
}
